import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Star } from "./brand/Logo";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PKGS = ["essential", "professional", "premium", "maintenance", "not_sure"];
const EMPTY = { name: "", email: "", phone: "", business: "", budget: "", timeline: "", website: "", details: "", hp: "" };
const field = "h-12 rounded-none border-0 border-b border-white/20 bg-transparent px-0 text-base text-ivory placeholder:text-white/25 focus-visible:border-lav focus-visible:ring-0 focus-visible:ring-offset-0";

const Field = ({ id, label, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="label text-mist">{label}</Label>
    {children}
    {error && <p className="text-sm text-[#ff8f8f]" data-testid={`form-error-${id}`}>{error}</p>}
  </div>
);

const PickSelect = ({ id, value, onChange, options, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger id={id} data-testid={`inquiry-${id}-select`} className={`${field} justify-between`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="border-white/10 bg-char text-ivory">
      {options.map(([v, l]) => (
        <SelectItem key={v} value={v} data-testid={`inquiry-${id}-option-${v}`} className="focus:bg-lav focus:text-ink">{l}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const Success = ({ onReset }) => {
  const { t } = useLang();
  return (
    <div className="flex min-h-[520px] flex-col items-start justify-center" data-testid="inquiry-success">
      <Star className="h-16 w-16 text-lav" cut />
      <h3 className="mt-10 font-display text-5xl font-bold uppercase leading-none tracking-tight text-ivory md:text-6xl">{t.form.successTitle}</h3>
      <p className="mt-6 max-w-md text-mist">{t.form.successBody}</p>
      <button onClick={onReset} className="label mt-10 text-lav underline-offset-4 hover:underline" data-testid="inquiry-send-another">{t.form.another}</button>
    </div>
  );
};

export const InquiryForm = () => {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const initPkg = PKGS.includes(params.get("package")) ? params.get("package") : "";
  const [v, setV] = useState({ ...EMPTY, package: initPkg });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverErr, setServerErr] = useState("");
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    const e = {};
    if (v.name.trim().length < 2) e.name = t.form.errors.name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = t.form.errors.email;
    if (v.details.trim().length < 10) e.details = t.form.errors.details;
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerErr("");
    if (!validate()) return;
    setStatus("sending");
    try {
      const payload = { ...v, package: v.package || "not_sure", language: lang };
      Object.keys(payload).forEach((k) => payload[k] === "" && k !== "hp" && delete payload[k]);
      await axios.post(`${API}/inquiries`, payload);
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setServerErr(err.response?.status === 429 ? t.form.errors.rate : t.form.errors.generic);
    }
  };

  if (status === "done") return <Success onReset={() => { setV({ ...EMPTY, package: "" }); setStatus("idle"); }} />;

  return (
    <form onSubmit={submit} noValidate className="grid gap-8 md:grid-cols-2" data-testid="inquiry-form">
      <Field id="name" label={t.form.name} error={errors.name}>
        <Input id="name" value={v.name} onChange={set("name")} className={field} autoComplete="name" data-testid="inquiry-name-input" />
      </Field>
      <Field id="email" label={t.form.email} error={errors.email}>
        <Input id="email" type="email" value={v.email} onChange={set("email")} className={field} autoComplete="email" data-testid="inquiry-email-input" />
      </Field>
      <Field id="phone" label={t.form.phone}>
        <Input id="phone" type="tel" value={v.phone} onChange={set("phone")} className={field} autoComplete="tel" data-testid="inquiry-phone-input" />
      </Field>
      <Field id="business" label={t.form.business}>
        <Input id="business" value={v.business} onChange={set("business")} className={field} autoComplete="organization" data-testid="inquiry-business-input" />
      </Field>
      <Field id="package" label={t.form.package}>
        <PickSelect id="package" value={v.package} onChange={set("package")} placeholder={t.form.select} options={PKGS.map((p) => [p, t.form.packages[p]])} />
      </Field>
      <Field id="budget" label={t.form.budget}>
        <PickSelect id="budget" value={v.budget} onChange={set("budget")} placeholder={t.form.select} options={t.form.budgets.map((b) => [b, b])} />
      </Field>
      <Field id="timeline" label={t.form.timeline}>
        <PickSelect id="timeline" value={v.timeline} onChange={set("timeline")} placeholder={t.form.select} options={t.form.timelines.map((b) => [b, b])} />
      </Field>
      <Field id="website" label={t.form.website}>
        <Input id="website" value={v.website} onChange={set("website")} className={field} placeholder="https://" data-testid="inquiry-website-input" />
      </Field>
      <div className="md:col-span-2">
        <Field id="details" label={t.form.details} error={errors.details}>
          <Textarea id="details" value={v.details} onChange={set("details")} rows={5} placeholder={t.form.detailsPh}
            className={`${field} h-auto min-h-[140px] resize-none py-3`} data-testid="inquiry-details-input" />
        </Field>
      </div>
      <input type="text" name="company_url" value={v.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <div className="flex flex-col gap-4 md:col-span-2 md:flex-row md:items-center md:justify-between">
        {serverErr ? <p className="text-sm text-[#ff8f8f]" data-testid="inquiry-server-error">{serverErr}</p> : <span />}
        <button type="submit" disabled={status === "sending"} data-testid="inquiry-submit-button"
          className="label group flex items-center justify-center gap-3 rounded-full bg-lav px-10 py-5 text-ink transition-colors duration-300 hover:bg-ivory disabled:opacity-60">
          {status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" />{t.form.sending}</> : <>{t.form.submit}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
        </button>
      </div>
    </form>
  );
};
